import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, Calendar, Zap, ArrowRight, Trash2, FileText, Clock } from 'lucide-react';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { concorrentesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useUpgradeModal } from '../../components/UpgradeModal';

const TONS = ['educativo', 'humorístico', 'polêmico', 'inspiracional'];

function IdeaCard({ ideia, index, darkMode, onExpand, onUsar, loadingRoteiro, tom, setTom }) {
  const tipoIcon = ideia.tipo === 'reels' ? '🎬' : ideia.tipo === 'carrossel' ? '📑' : '📷';
  const tipoLabel = ideia.tipo === 'reels' ? 'Reels' : ideia.tipo === 'carrossel' ? 'Carrossel' : 'Foto';
  const estrategiaColors = { 'contra-narrativa': 'bg-red-500/10 text-red-400 border-red-500/20', inspiracao: 'bg-green-500/10 text-green-400 border-green-500/20', tendencia: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };

  return (
    <div className={`group rounded-2xl border overflow-hidden transition-all hover:shadow-lg ${darkMode ? 'bg-zinc-800/80 border-zinc-700/50 hover:border-zinc-600' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center gap-2 border-b ${darkMode ? 'border-zinc-700/50' : 'border-gray-100'}`}>
        <span className="text-base">{tipoIcon}</span>
        <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tipoLabel}</span>
        {ideia.estrategia && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${estrategiaColors[ideia.estrategia] || ''}`}>
            {ideia.estrategia === 'contra-narrativa' ? '⚔️ Contra' : ideia.estrategia === 'inspiracao' ? '✨ Inspiração' : '📈 Tendência'}
          </span>
        )}
        {ideia.horario_sugerido && <span className={`text-[10px] ml-auto ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>⏰ {ideia.horario_sugerido}</span>}
      </div>

      <div className="p-4 space-y-3">
        <h3 className={`font-bold text-sm leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ideia.titulo}</h3>
        
        <p className={`text-xs leading-relaxed line-clamp-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{ideia.legenda}</p>

        {ideia.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {ideia.hashtags.slice(0, 6).map((h, j) => (
              <span key={j} className={`text-[10px] px-1.5 py-0.5 rounded-md ${darkMode ? 'bg-zinc-700/50 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>{h}</span>
            ))}
            {ideia.hashtags.length > 6 && <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>+{ideia.hashtags.length - 6}</span>}
          </div>
        )}

        {ideia.inspirado_em && <p className={`text-[10px] italic ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>💡 {ideia.inspirado_em}</p>}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <select value={tom} onChange={e => setTom(index, e.target.value)}
            className={`text-xs rounded-lg px-2 py-2 border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
            {TONS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <button onClick={() => onExpand(index, tom)} disabled={loadingRoteiro}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all">
            {loadingRoteiro ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {loadingRoteiro ? 'Gerando roteiro...' : 'Gerar roteiro'}
          </button>
        </div>
        <button onClick={() => onUsar(ideia, null)}
          className={`w-full py-2 rounded-lg text-xs font-medium transition-colors ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          📅 Agendar direto (sem roteiro)
        </button>
      </div>
    </div>
  );
}

function CalendarioView({ calendario, darkMode, onUsarDia }) {
  if (!calendario) return null;
  const propColors = {
    Educar: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    Engajar: 'from-green-500/20 to-green-600/10 border-green-500/30',
    Vender: 'from-red-500/20 to-red-600/10 border-red-500/30',
    Entreter: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30',
    Inspirar: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    Conectar: 'from-pink-500/20 to-pink-600/10 border-pink-500/30',
    Converter: 'from-orange-500/20 to-orange-600/10 border-orange-500/30',
  };

  return (
    <div className="space-y-4">
      {calendario.estrategia_geral && (
        <div className={`rounded-2xl p-5 text-sm leading-relaxed ${darkMode ? 'bg-gradient-to-r from-indigo-900/30 to-purple-900/30 text-indigo-300 border border-indigo-500/20' : 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border border-indigo-200'}`}>
          📋 <span className="font-semibold">Estratégia da semana:</span> {calendario.estrategia_geral}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {(calendario.dias || []).map((dia, i) => {
          const gradient = propColors[dia.proposito] || 'from-gray-500/20 to-gray-600/10 border-gray-500/30';
          return (
            <div key={i} className={`rounded-2xl border overflow-hidden ${darkMode ? 'bg-zinc-800/80' : 'bg-white'} shadow-sm`}>
              <div className={`px-4 py-3 bg-gradient-to-r ${gradient} flex items-center justify-between`}>
                <span className="text-sm font-bold">{dia.dia_semana}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{dia.proposito}</span>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center gap-1.5">
                  <span>{dia.tipo === 'reels' ? '🎬' : dia.tipo === 'carrossel' ? '📑' : dia.tipo === 'story' ? '📱' : '📷'}</span>
                  <span className={`font-semibold text-xs ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dia.titulo}</span>
                </div>
                <p className={`text-[11px] leading-relaxed line-clamp-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{dia.legenda}</p>
                <div className="flex items-center justify-between pt-1">
                  <span className={`text-[10px] flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}><Clock className="w-3 h-3" /> {dia.horario}</span>
                </div>
                {dia.dica && <p className={`text-[10px] italic ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>💡 {dia.dica}</p>}
                <button onClick={() => onUsarDia(dia)}
                  className="w-full py-2 mt-1 rounded-lg text-[11px] font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 transition-all">
                  📅 Criar post
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoteirosListView({ roteiros, darkMode, onOpen, onDelete }) {
  if (!roteiros?.length) return (
    <div className={`text-center py-12 rounded-2xl ${darkMode ? 'bg-zinc-800/50' : 'bg-white'} shadow-sm`}>
      <FileText className={`w-10 h-10 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nenhum roteiro salvo ainda</p>
      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Gere ideias e clique em "Gerar roteiro" para criar</p>
    </div>
  );

  const statusColors = { rascunho: 'bg-yellow-500/10 text-yellow-400', aprovado: 'bg-green-500/10 text-green-400', usado: 'bg-blue-500/10 text-blue-400' };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {roteiros.map(r => (
        <div key={r.id} className={`rounded-2xl border overflow-hidden cursor-pointer transition-all hover:shadow-lg ${darkMode ? 'bg-zinc-800/80 border-zinc-700/50 hover:border-zinc-600' : 'bg-white border-gray-200 hover:border-gray-300'}`}
          onClick={() => onOpen(r)}>
          <div className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{r.tipo === 'reels' ? '🎬' : r.tipo === 'carrossel' ? '📑' : '📷'}</span>
                <h3 className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{r.titulo}</h3>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColors[r.status] || ''}`}>{r.status}</span>
            </div>
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Tom: {r.tom} • {new Date(r.updated_at).toLocaleDateString('pt-BR')}
            </p>
            <div className="flex justify-end">
              <button onClick={e => { e.stopPropagation(); onDelete(r.id); }}
                className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-zinc-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function IdeaGenerator() {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ideias, setIdeias] = useState(() => {
    try { const s = sessionStorage.getItem('nomoz_ideias'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [tipoFilter, setTipoFilter] = useState({ benchmark: true, adversario: false, observacao: true });
  const [tab, setTab] = useState('ideias');
  const [tons, setTons] = useState({});
  const [loadingRoteiro, setLoadingRoteiro] = useState({});
  const [calendario, setCalendario] = useState(null);
  const [loadingCal, setLoadingCal] = useState(false);
  const { checkAndShowUpgrade, modal: upgradeModal } = useUpgradeModal();
  // Saved roteiros
  const [roteiros, setRoteiros] = useState([]);

  useEffect(() => { loadRoteiros(); }, []); // eslint-disable-line

  const loadRoteiros = async () => {
    try { const res = await concorrentesAPI.listRoteiros(); setRoteiros(res.data || []); } catch {}
  };

  const generate = async () => {
    setLoading(true);
    try {
      const tipos = Object.entries(tipoFilter).filter(([,v]) => v).map(([k]) => k).join(',');
      const res = await concorrentesAPI.gerarIdeias(days, tipos);
      setIdeias(res.data);
      sessionStorage.setItem('nomoz_ideias', JSON.stringify(res.data));
      setTons({});
    } catch (e) { if (!checkAndShowUpgrade(e)) toast.error(e.response?.data?.message || 'Analise concorrentes primeiro'); }
    finally { setLoading(false); }
  };

  const expandir = async (idx, tom) => {
    setLoadingRoteiro(p => ({ ...p, [idx]: true }));
    try {
      const res = await concorrentesAPI.expandirIdeia(ideias.ideias[idx], tom);
      const roteiro = res.data;
      // Navigate to editor page
      navigate(`/insights/ideias/roteiro/${roteiro.roteiro_id}`);
    } catch (e) { if (!checkAndShowUpgrade(e)) toast.error('Erro ao gerar roteiro'); }
    finally { setLoadingRoteiro(p => ({ ...p, [idx]: false })); }
  };

  const gerarCal = async () => {
    setLoadingCal(true);
    try {
      const tipos = Object.entries(tipoFilter).filter(([,v]) => v).map(([k]) => k).join(',');
      const res = await concorrentesAPI.gerarCalendario(days, tipos);
      setCalendario(res.data);
      setTab('calendario');
    } catch (e) { if (!checkAndShowUpgrade(e)) toast.error(e.response?.data?.message || 'Erro ao gerar calendário'); }
    finally { setLoadingCal(false); }
  };

  const usarIdeia = (ideia, roteiro) => {
    const legenda = roteiro?.legenda_final || ideia.legenda;
    const hashtags = roteiro?.hashtags_otimizadas || ideia.hashtags || [];
    navigate('/insights/publicacoes', { state: { prefill: { caption: legenda + '\n\n' + hashtags.join(' '), tipo: ideia.tipo === 'reels' ? 'video' : ideia.tipo === 'carrossel' ? 'carousel' : 'image', horario: ideia.horario_sugerido } } });
  };

  const usarDiaCal = (dia) => {
    navigate('/insights/publicacoes', { state: { prefill: { caption: dia.legenda + '\n\n' + (dia.hashtags || []).join(' '), tipo: dia.tipo === 'reels' ? 'video' : dia.tipo === 'carrossel' ? 'carousel' : 'image', horario: dia.horario } } });
  };

  const openSavedRoteiro = async (r) => {
    navigate(`/insights/ideias/roteiro/${r.id}`);
  };

  const deletarRoteiro = async (id) => {
    if (!window.confirm('Deletar este roteiro?')) return;
    try { await concorrentesAPI.deleteRoteiro(id); loadRoteiros(); toast.success('Roteiro deletado'); } catch { toast.error('Erro'); }
  };

  const setTom = (idx, tom) => setTons(p => ({ ...p, [idx]: tom }));

  const tabBtn = (key, label, count) => (
    <button key={key} onClick={() => setTab(key)}
      className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${tab === key
        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
        : darkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-zinc-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
      {label}{count != null ? ` (${count})` : ''}
    </button>
  );

  return (
    <Layout>
      {upgradeModal}
      <PageHeader title="Gerador de Ideias" subtitle="Ideias de conteúdo geradas por IA com base no seu nicho" />
      <div className="p-6 space-y-6">
        {/* Controls */}
        <div className={`rounded-2xl p-5 ${darkMode ? 'bg-zinc-800/50 border border-zinc-700/50' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="space-y-3">
              <div className="flex gap-1.5 flex-wrap">
                {[7, 15, 30, 60, 90].map(d => (
                  <button key={d} onClick={() => { setDays(d); setIdeias(null); setCalendario(null); sessionStorage.removeItem('nomoz_ideias'); }}
                    className={`px-3.5 py-1.5 text-sm rounded-xl transition-all ${days === d
                      ? 'bg-blue-600 text-white shadow-sm' : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    {d} dias
                  </button>
                ))}
              </div>
              <div className="flex gap-3 text-xs">
                {[
                  { key: 'benchmark', label: '📊 Benchmark' },
                  { key: 'adversario', label: '⚔️ Adversário' },
                  { key: 'observacao', label: '👁️ Observação' },
                ].map(t => (
                  <label key={t.key} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={tipoFilter[t.key]} onChange={(e) => setTipoFilter({ ...tipoFilter, [t.key]: e.target.checked })} className="rounded" />
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{t.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={generate} disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all shadow-md text-sm font-semibold">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Gerando...</> : <><Sparkles className="w-4 h-4" /> Gerar Ideias</>}
              </button>
              <button onClick={gerarCal} disabled={loadingCal}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 transition-all shadow-md text-sm font-semibold">
                {loadingCal ? <><Loader2 className="w-4 h-4 animate-spin" /> Gerando...</> : <><Calendar className="w-4 h-4" /> Calendário 7 dias</>}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {tabBtn('ideias', '✨ Ideias', ideias?.ideias?.length)}
          {calendario && tabBtn('calendario', '📅 Calendário', calendario.dias?.length)}
          {tabBtn('roteiros', '📝 Roteiros', roteiros.length)}
        </div>

        {/* Empty */}
        {tab === 'ideias' && !ideias && !loading && (
          <div className={`text-center py-20 rounded-2xl ${darkMode ? 'bg-zinc-800/30' : 'bg-white'} shadow-sm`}>
            <Sparkles className={`w-14 h-14 mx-auto mb-4 ${darkMode ? 'text-gray-700' : 'text-gray-300'}`} />
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Clique em "Gerar Ideias" ou "Calendário 7 dias"</p>
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>É necessário ter concorrentes analisados</p>
          </div>
        )}

        {/* Loading */}
        {(loading || loadingCal) && (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-purple-500" />
            <p className={`mt-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {loading ? 'A IA está analisando seu nicho e gerando ideias...' : 'Gerando calendário editorial...'}
            </p>
          </div>
        )}

        {/* Ideas grid */}
        {tab === 'ideias' && ideias?.ideias && !loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ideias.ideias.map((ideia, i) => (
              <IdeaCard key={i} ideia={ideia} index={i} darkMode={darkMode}
                onExpand={expandir} onUsar={usarIdeia}
                loadingRoteiro={!!loadingRoteiro[i]}
                tom={tons[i] || 'educativo'} setTom={setTom} />
            ))}
          </div>
        )}

        {/* Calendar */}
        {tab === 'calendario' && calendario && !loadingCal && (
          <CalendarioView calendario={calendario} darkMode={darkMode} onUsarDia={usarDiaCal} />
        )}

        {/* Saved roteiros */}
        {tab === 'roteiros' && (
          <RoteirosListView roteiros={roteiros} darkMode={darkMode} onOpen={openSavedRoteiro} onDelete={deletarRoteiro} />
        )}
      </div>
    </Layout>
  );
}
